import { Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import { BemessungenMikropfaehle } from "../../../store/slices/bemessungenMikropfaehleSlice";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import KiloNewtonDividedByMillimetreSquared from "../../Math/Units/Metric/KiloNewtonDividedByMillimetreSquared";
import Metre from "../../Math/Units/Metric/Metre";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import MillimetreSquared from "../../Math/Units/Metric/MillimetreSquared";
import { DataRow } from "../DataRow";
import InvoiceTableBlankSpace from "../InvoiceTableBlankSpace";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export function GermanyInnereDruckKnickenDinEN1993({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;
  return (
    <PageWrapper Deckblatt={data.Deckblatt}>
      <View>
        {/* Pfahlkopf / Übergangsrohr */}
        <Text style={styles.section}>
          {data.Deckblatt.BemessungenMikropfaehle === BemessungenMikropfaehle.EUROCODE_7
            ? t("inner.buckling.buttonDinEN1993_ec7")
            : t("inner.buckling.buttonDinEN1993")}
        </Text>

        {/* Parameter Stahltragglied */}
        <Text style={styles.sectionSubText}>
          {t("inner.buckling.dinEN1993.parameterTragglied.headline")}
        </Text>

        {/* (Daten aus Zulassung, bzw. aus Zulassung umgerechnet) */}
        <Text style={styles.sectionSubText3}>
          {t("inner.buckling.ofner.parameterTragglied.headlineSub")}
        </Text>

        <View style={tableStyles.tableContainer}>
          {/* Querschnitt */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.parameterTragglied.crossSection"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Querschnitt,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}A_{s}",
                height: 7,
                width: 9,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <MillimetreSquared />,
            }}
          />
          {/* Innendurchmesser */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.innerDiameter"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Innendurchmesser,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}D_{i}",
                height: 7,
                width: 9,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 1,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />
          {/* Äußerer Durchmesser */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.outerDiameter"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.aeußererDurchmesser,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}D_{a,cal}",
                height: 9,
                width: 21,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 1,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              // "https://latex.codecogs.com/png.image?\dpi{110}&space;\bg_white&space;\inline&space;D_{a,cal}=\sqrt{{\frac{4\cdot&space;A_s}{\pi}}&plus;{D_i^2}" title="\bg_white \inline D_{a,cal}=\sqrt{{\frac{4\cdot A_s}{\pi}}+{D_i^2}"
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;D_{a,cal}=\\sqrt{{\\frac{4\\cdot&space;A_s}{\\pi}}&plus;{D_i}^2}",
                height: 15,
                width: 78,
              },
            }}
          />
          {/* Dehnsteifigkeit */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.axialStiffness"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Dehnsteifigkeit,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}EA_{s}",
                height: 7,
                width: 17,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />
          {/* Elastizitätsmodul */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.modulusOfElasticity"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Elastizitaetsmodul,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}E",
                height: 7,
                width: 7,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: "kN/mm²",
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;E=\\frac{EA_s}{A_s}",
                height: 11,
                width: 31,
              },
            }}
          />
          {/* Trägheitsmoment */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.momentOfInertia"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Traegheitsmoment,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}I",
                height: 6,
                width: 4,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              unitImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\textbf{mm}^4",
                height: 7,
                width: 19,
              },
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;I=\\frac{\\pi}{64}\\cdot(D_{a,cal}^4-D_i^4)",
                height: 11,
                width: 75,
              },
            }}
          />
          {/* Biegesteifigkeit */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.flexuralStiffness"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Biegesteifigkeit,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}&space;EI",
                height: 6,
                width: 11,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: "kNmm²",
            }}
          />
          {/* Imperfektionsbeiwert */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.imperfectionFactor"),
              value: data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Imperfektionsbeiwert,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}&space;\\alpha",
                height: 4,
                width: 5,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
            }}
          />
          {/* charakteristische Festigkeit */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.characteristicStrengh"),
              value:
                data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied
                  .charakteristischeFestigkeit,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}&space;f_{y,k}",
                height: 9,
                width: 13,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 3,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewtonDividedByMillimetreSquared />,
            }}
          />
          {/* Teilsicherheitsbeiwert */}
          <DataRow
            item={{
              title: t("inner.buckling.ofner.parameterTragglied.partialSafetyFactor"),
              value:
                data.Germany.Innere.Knicken.dinEN.ParameterStahltragglied.Teilsicherheitsbeiwert,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}&space;\\gamma_{\\tiny_{M}}",
                height: 6,
                width: 11,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
            }}
          />
          {/* <InvoiceTableBlankSpace rowsCount={27 - itemIndex} /> */}
        </View>

        {/* Nachweise */}
        <Text style={styles.sectionSubText}>
          {t("inner.buckling.dinEN1993.verification.headline")}
        </Text>
        <View style={tableStyles.tableContainer}>
          {/* Dicke weiche Bodenschicht */}
          <DataRow
            item={{
              title: t("inner.buckling.freeLength"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.dickeWeicheBodenschicht,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L",
                height: 6,
                width: 5,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Metre />,
            }}
          />

          {/* Knicklängenbeiwert */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.bucklingCoefficient"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.knicklaengenbeiwert,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\beta",
                height: 8,
                width: 5,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 1,
              itemIndex: itemIndex++,
            }}
          />

          {/* Knicklänge */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.criticalBucklingLength"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.knicklaenge,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{cr}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L_{cr}",
                height: 7,
                width: 11,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{cr}=\\beta\\cdot&space;L
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}&space;L_{cr}=\\beta\\cdot&space;L",
                height: 8,
                width: 40,
              },
            }}
          />

          {/* Trägheitsradius */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.radiusInertia"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.traegheitsradius,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;i",
                height: 6,
                width: 3,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}i=\\sqrt{\\frac{I}{A_s}}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}i=\\sqrt{\\frac{I}{A_s}}",
                height: 15,
                width: 32,
              },
            }}
          />

          {/* Bezugschlankheitsgrad */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.referencedSlendernessRatio"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.bezugschlankheitsgrad,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\lambda_{1}",
                height: 7,
                width: 8,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              //valueUnit: <Millimetre />,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\lambda_{1}=\\pi\\cdot&space;\\sqrt{\\frac{E}{f_{y,k}}}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\lambda_{1}=\\pi\\cdot&space;\\sqrt{\\frac{E}{f_{y,k}}}",
                height: 15,
                width: 52,
              },
            }}
          />

          {/* bezogener Schlankheitsgrad */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.slenderness"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.bezogenerSchlankheitsgrad,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\overline{\\lambda}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\overline{\\lambda}",
                height: 7,
                width: 5,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              //\\overline{\\lambda}=\\frac{L_{cr}}{i\\cdot\\lambda_{1}}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\overline{\\lambda}=\\frac{L_{cr}}{i\\cdot\\lambda_{1}}",
                height: 11,
                width: 28,
              },
            }}
          />

          {/* Abminderungsfaktor */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.reductionFactor"),
              value:
                data.Germany.Innere.Knicken.dinEN.Nachweise.Abminderungsfaktor.abminderungsfaktor,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\chi",
                height: 6,
                width: 5,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              //valueUnit: <Millimetre />,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\chi=\\frac{1}{\\Phi&plus;\\sqrt{\\Phi^2&plus;\\lambda^2}}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\chi=\\frac{1}{\\Phi&plus;\\sqrt{\\Phi^2&plus;\\lambda^2}}",
                height: 13,
                width: 52,
              },
            }}
          />

          {/* Abminderungsfaktor 2*/}
          <DataRow
            item={{
              //title: t("inner.buckling.dinEN1993.verification.reductionFactor"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.Abminderungsfaktor.phi,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\Phi",
                height: 6,
                width: 6,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 1,
              itemIndex: itemIndex++,
              //valueUnit: <Millimetre />,
              // \Phi=0,5\cdot \left [ 1+\alpha \cdot \left ( \overline{\lambda} - 0,2 \right ) + \overline{\lambda}^2 \right ]
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\Phi=0,5\\cdot&space;\\left&space;[&space;1&plus;\\alpha&space;\\cdot&space;\\left&space;(&space;\\overline{\\lambda}&space;-&space;0,2&space;\\right&space;)&space;&plus;&space;\\overline{\\lambda}^2&space;\\right&space;]
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\Phi=0,5\\cdot&space;\\left&space;[&space;1&plus;\\alpha&space;\\cdot&space;\\left&space;(&space;\\overline{\\lambda}&space;-&space;0,2&space;\\right&space;)&space;&plus;&space;\\overline{\\lambda}^2&space;\\right&space;]",
                height: 15,
                width: 121,
              },
            }}
          />

          {/* Bemessungswert des Widerstandes gegen Biegeknicken */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.designValueFlexuralBuckling"),
              value:
                data.Germany.Innere.Knicken.dinEN.Nachweise.bemessungswertWiderstandeBiegeknicken,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{b,Rd}",
                height: 9,
                width: 19,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />

          {/* Ausnutzung */}
          <DataRow
            item={{
              title: t("inner.buckling.dinEN1993.verification.utilizationFactor"),
              value: data.Germany.Innere.Knicken.dinEN.Nachweise.ausnutzungs,
              //
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta",
                height: 6,
                width: 4,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              //valueUnit: <KiloNewton />,
              //https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\eta=\frac{F_d}{N_{b,Rd}}
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta=\\frac{F_d}{N_{b,Rd}}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta=\\frac{F_d}{N_{b,Rd}}",
                height: 13,
                width: 33,
              },
            }}
          />
          <InvoiceTableBlankSpace rowsCount={25 - itemIndex} />
        </View>
      </View>
    </PageWrapper>
  );
}
